import React, { useContext, useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import apiClient from './api';
import AuthContext from './context/AuthContext';
import {
    Container, Typography, Box, Button, Chip, Checkbox,
    FormControlLabel, CircularProgress, Alert, Divider, Stack,
} from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassTop';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PaymentsIcon from '@mui/icons-material/Payments';
import PhoneIcon from '@mui/icons-material/Phone';
import moment from 'jalali-moment';
import { formatToman } from './labels';
import { glass, brand } from './theme';
import { SectionHeader } from './components/SectionHeader';

function ContractView() {
    const { requestId } = useParams();
    const { user } = useContext(AuthContext);
    const [contract, setContract] = useState(null);
    const [loading, setLoading] = useState(true);
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [paying, setPaying] = useState(false);
    const [payError, setPayError] = useState('');

    const fetchContract = () => {
        apiClient.get(`/api/contracts/request/${requestId}/`)
            .then((res) => setContract(res.data))
            .catch(() => setError('قراردادی برای این درخواست یافت نشد یا هنوز ایجاد نشده است.'))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchContract(); }, [requestId]);

    const isRequester = user?.user_type === 'requester';
    const mySignedAt = isRequester ? contract?.requester_accepted_at : contract?.provider_accepted_at;
    const otherSignedAt = isRequester ? contract?.provider_accepted_at : contract?.requester_accepted_at;
    const otherLabel = isRequester ? 'همیار' : 'درخواست‌دهنده';
    const counterpartName = isRequester ? contract?.provider_name : contract?.requester_name;
    const counterpartPhone = isRequester ? contract?.provider_phone : contract?.requester_phone;

    const handleAccept = async () => {
        setSubmitting(true);
        setError('');
        try {
            const res = await apiClient.post(`/api/contracts/request/${requestId}/accept/`);
            setContract(res.data);
        } catch (e) {
            setError('خطا در ثبت پذیرش قرارداد. لطفاً دوباره تلاش کنید.');
        } finally {
            setSubmitting(false);
        }
    };

    const handlePay = async () => {
        setPaying(true);
        setPayError('');
        try {
            const res = await apiClient.post('/api/payments/request/', { request_id: requestId });
            window.location.href = res.data.pay_url;
        } catch (e) {
            setPayError(e.response?.data?.detail || 'خطا در اتصال به درگاه پرداخت. لطفاً دوباره تلاش کنید.');
            setPaying(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    }

    if (error && !contract) {
        return (
            <Box sx={{ background: glass.canvasGradient, minHeight: '100%', py: 6 }}>
                <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
                    <Alert severity="warning" sx={{ borderRadius: '12px' }}>{error}</Alert>
                    <Button component={RouterLink} to="/dashboard" sx={{ mt: 2 }}>بازگشت به داشبورد</Button>
                </Container>
            </Box>
        );
    }

    return (
        <Box sx={{ background: glass.canvasGradient, minHeight: '100%', pb: 6 }}>
            <Container maxWidth="md" sx={{ pt: 3 }}>
                <Button component={RouterLink} to="/dashboard" startIcon={<ArrowForwardIcon />} size="small" sx={{ mb: 2, color: 'text.secondary' }}>
                    بازگشت به داشبورد
                </Button>

                <Box sx={{ ...glass.card, p: { xs: 2.5, md: 4 }, mb: 3 }}>
                    <SectionHeader icon={GavelIcon} title={`قرارداد خدمت — درخواست #${requestId}`} subtitle="پلتفرم الو یارا واسط معرفی و پرداخت است؛ قرارداد میان شما و طرف مقابل منعقد می‌شود." />

                    <Box sx={{ display: 'flex', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                        <Chip
                            icon={mySignedAt ? <CheckCircleIcon /> : <HourglassEmptyIcon />}
                            color={mySignedAt ? 'primary' : 'default'}
                            label={mySignedAt ? `شما پذیرفتید — ${moment(mySignedAt).locale('fa').format('D MMMM YYYY، HH:mm')}` : 'در انتظار پذیرش شما'}
                        />
                        <Chip
                            icon={otherSignedAt ? <CheckCircleIcon /> : <HourglassEmptyIcon />}
                            variant="outlined"
                            color={otherSignedAt ? 'primary' : 'default'}
                            label={otherSignedAt ? `${otherLabel} پذیرفت — ${moment(otherSignedAt).locale('fa').format('D MMMM YYYY، HH:mm')}` : `در انتظار پذیرش ${otherLabel}`}
                        />
                    </Box>
                </Box>

                <Box sx={{ ...glass.card, p: { xs: 2.5, md: 4 }, mb: 3 }}>
                    <Typography
                        component="pre"
                        sx={{
                            whiteSpace: 'pre-wrap', fontFamily: 'inherit', fontSize: '0.95rem',
                            lineHeight: 2, m: 0, maxHeight: 420, overflowY: 'auto', pr: 1,
                        }}
                    >
                        {contract?.legal_text}
                    </Typography>
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}

                {!mySignedAt && (
                    <Box sx={{ ...glass.card, p: { xs: 2.5, md: 4 }, mb: 3 }}>
                        <FormControlLabel
                            control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
                            label="متن قرارداد را به طور کامل مطالعه کردم و مفاد آن را می‌پذیرم."
                        />
                        <Divider sx={{ my: 1.5, borderColor: 'rgba(15,51,43,0.1)' }} />
                        <Button
                            variant="contained" size="large" fullWidth
                            disabled={!agreed || submitting}
                            onClick={handleAccept}
                        >
                            {submitting ? <CircularProgress size={22} color="inherit" /> : 'پذیرفتن قرارداد'}
                        </Button>
                    </Box>
                )}

                {contract?.is_fully_signed && (
                    <Box sx={{ ...glass.card, p: { xs: 2.5, md: 4 } }}>
                        <SectionHeader icon={PaymentsIcon} title="پرداخت و شروع همکاری" />

                        {!contract.is_paid ? (
                            isRequester ? (
                                <Stack spacing={2}>
                                    <Typography color="text.secondary">
                                        قرارداد توسط هر دو طرف پذیرفته شد. با پرداخت مبلغ توافق‌شده، همکاری آغاز می‌شود و مشخصات تماس همیار برای هماهنگی در اختیار شما قرار می‌گیرد.
                                    </Typography>
                                    <Box sx={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5,
                                        p: 2, borderRadius: '14px', bgcolor: brand.accentTint, border: `1px solid ${brand.accent}55`,
                                    }}>
                                        <Typography sx={{ fontWeight: 800 }}>مبلغ قابل پرداخت</Typography>
                                        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', color: brand.accentDeep }}>
                                            {formatToman(contract.amount)}
                                        </Typography>
                                    </Box>
                                    {payError && <Alert severity="error" sx={{ borderRadius: '12px' }}>{payError}</Alert>}
                                    <Button variant="contained" size="large" fullWidth disabled={paying} onClick={handlePay}>
                                        {paying ? <CircularProgress size={22} color="inherit" /> : 'پرداخت و شروع همکاری'}
                                    </Button>
                                </Stack>
                            ) : (
                                <Alert severity="info" sx={{ borderRadius: '12px' }}>
                                    قرارداد نهایی شد — در انتظار پرداخت درخواست‌دهنده هستید. پس از پرداخت، مشخصات تماس برای هماهنگی نمایش داده می‌شود.
                                </Alert>
                            )
                        ) : (
                            <Stack spacing={2}>
                                <Alert severity="success" sx={{ borderRadius: '12px' }}>
                                    پرداخت با موفقیت انجام شد — همکاری آغاز شده است.
                                </Alert>
                                <Box sx={{
                                    display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: '14px',
                                    bgcolor: brand.primaryTint,
                                }}>
                                    <Box sx={{
                                        width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center',
                                        justifyContent: 'center', bgcolor: brand.primary, color: '#fff', flexShrink: 0,
                                    }}>
                                        <PhoneIcon fontSize="small" />
                                    </Box>
                                    <Box sx={{ flexGrow: 1 }}>
                                        <Typography sx={{ fontWeight: 700 }}>{counterpartName}</Typography>
                                        <Typography variant="body2" color="text.secondary">برای هماهنگی تماس بگیرید</Typography>
                                    </Box>
                                    <Button variant="contained" href={`tel:${counterpartPhone}`} sx={{ flexShrink: 0 }}>
                                        {counterpartPhone}
                                    </Button>
                                </Box>
                            </Stack>
                        )}
                    </Box>
                )}
            </Container>
        </Box>
    );
}

export default ContractView;
